<template>
    <hts-drawer
        v-model="visible"
        size="medium"
        :rules="rules"
        :title="$t('labels.addNodeConnection')"
        :submit="submitAddNodeConnection"
        @close="close()">
        <hts-card :title="$t('labels.generalInformation')">
            <div
                v-loading="!visible"
                class="grid gap-4 grid-cols-1">
                <hts-form-input
                    v-model="startNode.name"
                    :label="$t('labels.startNode')"
                    prop="startNode"
                    required
                    disabled />

                <span>
                    {{ $t('labels.businessConnection') }}
                    <span class="text-error-500 ml-1">
                        *
                    </span>
                </span>
                <div
                    v-for="business in businessData"
                    :key="business">
                    <hts-form-disclosure
                        v-if="Object.keys(startNode.businesses).includes(business._id)"
                        color="main"
                        size="large"
                        :is-have-children="true">
                        <template #reference>
                            <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white bg-main-500 hover:bg-main-600 w-full">
                                <input
                                    type="checkbox"
                                    class="w-[17.5px] h-[17.5px]"
                                    :checked="Object.keys(dataAdd.businesses)?.includes(business._id)"
                                    :value="business._id"
                                    @click="(e) => businessClick(e, business._id)" >
                                <span>
                                    {{ business.name }}
                                </span>
                            </div>
                        </template>
                        <template #children>
                            <div
                                v-for="(task, subIndex) in business.tasks"
                                :key="task">
                                <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white mt-3 ml-10 p-2 pl-3 bg-main-400">
                                    <input
                                        class="w-[17.5px] h-[17.5px]"
                                        type="checkbox"
                                        disabled
                                        :checked="Object.keys(startNode.businesses).includes(business._id) && startNode.businesses[business._id]?.includes(task._id)"
                                        :value="task._id" >
                                    <span>
                                        {{ task.name }}
                                    </span>
                                </div>
                            </div>
                        </template>
                    </hts-form-disclosure>
                </div>

                <hts-form-select
                    v-model="dataAdd.nodeConnection"
                    :query="findNodeConnection"
                    :filterable="true"
                    :clearable="true"
                    :param-prepare="dataAdd.businesses"
                    :label="$t('labels.nodeConnection')"
                    required
                    multiple
                    prop="nodeConnection"
                    key-id="_id"
                    key-label="name">
                    <template #custom>
                        <hts-tooltip class="mt-2 ml-2">
                            <div>
                                <span class="font-bold">
                                    {{ $t('labels.noteNodeConnection') }}
                                </span>
                            </div>
                            <template #reference>
                                <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                            </template>
                        </hts-tooltip>
                    </template>
                </hts-form-select>
            </div>
        </hts-card>
    </hts-drawer>
</template>

<script setup lang="ts">
const emits = defineEmits(['refreshTables'])
const { $disableReactive } = useNuxtApp()

const visible = ref(false)
const nodeId = ref('')
const startNode = ref({
  name: '',
  nodeType: '',
  businesses: {} as { [key: string]: string[] }
})
const dataAdd = ref({
  startNode: '',
  businesses: {} as { [key: string]: string[] },
  nodeConnection: '' as string | string[]
})
const businessData = ref<{ [key: string]: string }[]>([])
const rules = {
  startNode: 'required',
  nodeConnection: 'required|requiredMultiple'
}

const open = async (id: string) => {
  nodeId.value = id
  dataAdd.value.startNode = id
  await getData()
  visible.value = true
}

const close = () => {
  visible.value = false
}

const resetField = () => {
  nodeId.value = ''

  startNode.value.name = ''
  startNode.value.nodeType = ''
  startNode.value.businesses = {}

  dataAdd.value.startNode = ''
  dataAdd.value.businesses = {}
  dataAdd.value.nodeConnection = ''
}

const getData = async () => {
  if (!nodeId.value) {
    return
  }
  const api = $urlAdmin.node.getById
  const data: any = await $api(api, { uriParams: { ID: nodeId.value } })
  const resData: any = data?.data?.value
  if (resData?.success) {
    startNode.value.name = resData.data?.location?.locationName || ''
    startNode.value.nodeType = resData.data?.nodeType?._id || ''
    startNode.value.businesses = $disableReactive(resData.data?.businesses) || {}
    dataAdd.value.businesses = $disableReactive(resData.data?.businesses) || {}
  }
}

watch(
  () => startNode.value.nodeType,
  async (val) => {
    // lấy thông tin nghiệp vụ
    const api = $urlAdmin.business.getByNodeType
    if (!val) {
      return
    }

    const data: any = await $api(api, { uriParams: { ID: val } })
    const resData = data?.data?.value
    if (resData?.success) {
      // data update UI
      businessData.value = resData.data
    }
  }
)

watch(
  () => dataAdd.value.businesses,
  (val) => {
    if (Array.isArray(val) && val.length === 0) {
      return
    }
    dataAdd.value.nodeConnection = ''
  },
  { deep: true }
)

const findNodeConnection = async (s: string) => {
  const api = $urlAdmin.node.findNodeConnections

  const res = await $api(api, {
    uriParams: {
      KEY: s,
      LIMIT: 100
    },
    body: dataAdd.value
  })
  const resData: any = res?.data?.value
  if (resData?.success) {
    return resData.data
  }
  return null
}

const submitAddNodeConnection = () => {
  if (dataAdd.value?.businesses && Object.keys(dataAdd.value?.businesses)?.length === 0) {
    process.client && $toast().error($t('labels.pleaseSelectBusinessConnection'))
    return
  }

  const api = $urlAdmin.node.connection.add
  $api(api, { body: dataAdd.value }).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      process.client && $toast().success($t('labels.addSuccess'))
      resetField()
      close()
      emits('refreshTables')
    }
  })
}

const businessClick = (e: any, id: string) => {
  e.stopPropagation()
  if (!Object.keys(dataAdd.value.businesses)?.includes(id)) {
    dataAdd.value.businesses[id] = [...startNode.value.businesses[id]]
  } else {
    delete dataAdd.value.businesses[id]
  }
}

defineExpose({
  open
})
</script>
